/* eslint-disable @typescript-eslint/no-non-null-assertion */
import { BigNumber } from '@ethersproject/bignumber';
import { defineStore } from 'pinia';
import { computed, ref, watch } from 'vue';
import { setProviderEventsHandler } from '@manifoldxyz/dropsite-lib';
import { detectManifoldEthereumProvider } from '@manifoldxyz/frontend-provider-utils';
import { GiveawayRequirementType, GiveawaySignupResponse } from '@manifoldxyz/redeem-client';
import { redeemApi } from '@/api/apiHelpers';
import { getTwitterHandle } from '@/api/creator';
import { resolveENSFromBadges } from '@/lib/address';
import { formatAddress } from '@/lib/claimUtil';

export type Creator = {
  id: number;
  address?: string;
  name: string;
  image?: string | null;
  twitterUrl?: string;
};

// Union of all the potential data fields from the different requirement types.
export type InstanceRequirement = {
  link?: string;
  network?: number;
  active?: boolean;
} | null;

export type InstanceRequirements = {
  [key in GiveawayRequirementType]?: Array<InstanceRequirement>;
};

export type InstanceResponse = {
  id: number;
  appId: number;
  slug: string;
  creator: Creator;
  publicData: {
    title: string;
    thumbnail: string;
    description: string;
    startDate: number;
    endDate: number;
    requirements: InstanceRequirements;
    allowlistRequirement?: { merkleTreeId: number };
    allowlistRestriction?: 'Allowlist' | 'None';
    allowlistLink?: string;
    allowlistDescription?: string;
    maxEntriesRestriction?: 'Limited' | 'Unlimited';
    maxEntries?: number;
  };
};

export type RequirementWithCollectorData = {
  type: GiveawayRequirementType;
  link?: string;
  network?: number;
  userData: any;
  complete: boolean;
  error?: string;
  index: number;
};

export const TWITTER_REQUIREMENTS = [
  GiveawayRequirementType.TWITTER_FOLLOW,
  GiveawayRequirementType.TWITTER_RETWEET,
  GiveawayRequirementType.TWITTER_LIKE,
];

export const useClaimStore = defineStore('claim', () => {
  ///
  // STATE
  //
  // Note: Setting a default state is required. Many
  // values are overwritten at time of initialization but the
  // typings they establish are important.
  //
  // If after initialization a value is guaranteed to
  // not be `undefined`, set a default value here.
  ///

  // instance data
  const id = ref(0);
  const appId = ref(0);
  const title = ref('');
  const image = ref('');
  const description = ref('');
  const slug = ref('');
  const creator = ref<Creator>({} as Creator);
  const startDate = ref<Date | null>(null);
  const endDate = ref<Date | null>(null);
  const totalEntries = ref<number>();
  const maxEntries = ref<number>();
  const instanceRequirements = ref<InstanceRequirements>();
  const allowlistRequirement = ref<{ merkleTreeId: number } | undefined>();
  const allowlistEnabled = ref<boolean>();
  const allowlistDescription = ref<string>();
  const allowlistLink = ref<string>();

  // store data
  const initializedInstanceData = ref(false);
  const initialized = ref(false);
  // default to mainnet (which we do anyway -- but we avoid undefined when we declare it here)
  const networkId = ref<number>(1);
  const creatorEnsOrFormattedWalletAddress = ref<string>();
  const walletAddress = ref<string>();
  const isProviderAvailable = ref(false);
  const ensOrFormattedWalletAddress = ref<string>();
  const balance = ref<BigNumber>();
  const activeNetwork = ref<number>();
  const isLoggedIn = ref(false);
  const subscribed = ref<boolean>(false);
  const currentEntries = ref<number>(0);

  // Query from redeem-server, auto-update once user logged in
  const signup = ref<GiveawaySignupResponse>();

  ///
  // ACTIONS
  ///
  function preprocessRequirements(requirements: InstanceRequirements) {
    // Preprocess requirements to remove any null or inactive requirements
    const processedRequirements: InstanceRequirements = {};
    for (const [key, value] of Object.entries(requirements)) {
      const originalRequirements = requirements[key as GiveawayRequirementType];
      processedRequirements[key as GiveawayRequirementType] = originalRequirements?.map((_, i) => {
        if (value[i]?.active === false) {
          return null;
        }
        return value[i];
      });
    }
    return processedRequirements;
  }

  async function initialize(instance: InstanceResponse) {
    _initializeInstanceData(instance);

    initializedInstanceData.value = true;

    await _initializeProvider();

    // Initialize ENS and Twitter for creator of the claim
    if (creator.value.address) {
      resolveENSFromBadges(creator.value.address || '').then((resolvedEns) => {
        creatorEnsOrFormattedWalletAddress.value =
          resolvedEns || formatAddress(creator.value.address || '');
      });
    }
    if (creator.value.address) {
      getTwitterHandle(creator.value.address || '').then((handle) => {
        creator.value.twitterUrl = handle;
      });
    }

    initialized.value = true;
  }

  function _initializeInstanceData(instance: InstanceResponse) {
    id.value = instance.id;
    appId.value = instance.appId;
    slug.value = instance.slug;
    creator.value = instance.creator;

    const publicData = instance.publicData;
    title.value = publicData.title;
    image.value = publicData.thumbnail;
    description.value = publicData.description;
    startDate.value = publicData.startDate ? new Date(publicData.startDate) : null;
    endDate.value = publicData.endDate ? new Date(publicData.endDate) : null;
    instanceRequirements.value = preprocessRequirements(publicData.requirements);
    maxEntries.value =
      publicData.maxEntriesRestriction === 'Limited' ? publicData.maxEntries : undefined;

    // Due to restrictions in current studio-apps schema, we put allowelistRequirement in publicData as a separate field rather than within the requirements object
    allowlistRequirement.value = publicData.allowlistRequirement;
    allowlistEnabled.value = publicData.allowlistRestriction === 'Allowlist';
    allowlistDescription.value = publicData.allowlistDescription;
    allowlistLink.value = publicData.allowlistLink;

    if (publicData.requirements.email) {
      // There should be only 1 email requirement, does not make sense to have more than 1
      if (publicData.requirements.email.length > 1) {
        throw new Error('Something went wrong, there should be only 1 email requirement');
      }
    }
  }

  async function _initializeProvider(fallbackProviderValue?: string) {
    await detectManifoldEthereumProvider({ initialized: false });
    if ((window.ManifoldEthereumProvider as any)._isInitialized !== undefined) {
      // Legacy version of connect widget
      await (window.ManifoldEthereumProvider as any).initialize(
        networkId.value,
        fallbackProviderValue
      );
    } else {
      await window.ManifoldEthereumProvider.initialize({
        network: networkId.value,
        fallbackHost: fallbackProviderValue,
        browserProviderTimeout: 500,
        browserProviderIgnoreDisconnect: true,
      });
    }
    const provider = window.ManifoldEthereumProvider.provider();
    const signingProvider = window.ManifoldEthereumProvider.provider(true);

    if (signingProvider && provider !== signingProvider) {
      isProviderAvailable.value = false;
    } else {
      isProviderAvailable.value = !!provider;
    }

    setProviderEventsHandler(async () => {
      const eth = window.ManifoldEthereumProvider;
      const currentProvider = eth.provider();
      const currentSigningProvider = eth.provider(true);
      const address = eth.selectedAddress();
      const chainId = eth.chainId();

      walletAddress.value = address;
      // Initialize ENS for connected wallet (buyer) of the claim
      const resolvedEns = await resolveENSFromBadges(address || '');
      ensOrFormattedWalletAddress.value = resolvedEns || formatAddress(address || '');

      try {
        balance.value = await eth.provider()?.getBalance(address || '');
      } catch {
        balance.value = undefined;
      }

      /**
       * NOTE: If a browser provider like MetaMask is available
       * chainId will be present, however with WalletConnect
       * there is often no provider available and therefore no chainId
       * either. We can only rely on a the claim's network.
       */
      activeNetwork.value = chainId || networkId.value;

      if (currentSigningProvider && currentProvider !== currentSigningProvider) {
        isProviderAvailable.value = false;
      } else {
        isProviderAvailable.value = !!currentProvider;
      }
    });

    window.addEventListener('m-authenticated', async (_) => {
      isLoggedIn.value = true;
    });
    window.addEventListener('m-unauthenticated', async (_) => {
      isLoggedIn.value = false;
    });
  }

  async function refreshSignup(force = false) {
    if (!!isLoggedIn.value && (!signup.value || force)) {
      try {
        signup.value = await redeemApi.clientLib.getSignup(id.value);
      } catch (e: unknown) {
        const error = e as { response: { data: { message: string } } };
        if (error?.response?.data?.message?.includes('not found')) {
          // create sign up if doesn't exist
          try {
            signup.value = await redeemApi.clientLib.createSignup(id.value);
          } catch (e: unknown) {
            const error = e as { response: { data: { message: string } } };
            if (error?.response?.data?.message?.includes('reached max signups')) {
              // max signups reached, setting totalEntries to be the same as maxEntries so UI shows as sold out
              maxEntries.value = currentEntries.value;
            }
          }
        }
      }
    } else if (!isLoggedIn.value) {
      signup.value = undefined;
    }
  }

  async function refreshCurrentSignups() {
    try {
      const currentSignupsResponse = await redeemApi.clientLib.getCurrentSignups(id.value);
      totalEntries.value = currentSignupsResponse.signups;
    } catch (e: unknown) {
      totalEntries.value = undefined;
    }
  }

  const status = computed(() => {
    const now = Date.now();
    const twitterRequirements = flatRequirements.value.filter((r) =>
      TWITTER_REQUIREMENTS.includes(r.type)
    );

    // If start date is in the future or any twitter requirement is not complete, we consider the claim not started
    if (
      (startDate.value && startDate.value.getTime() > now) ||
      twitterRequirements.some((r) => !r.link)
    ) {
      return 'not-started';
    } else if (endDate.value && endDate.value.getTime() < now) {
      return 'ended';
    } else if (maxEntries.value !== undefined && currentEntries.value >= maxEntries.value) {
      return 'full';
    } else {
      return 'active';
    }
  });

  const isSoldOut = computed(() => {
    if (typeof maxEntries.value !== 'undefined' || maxEntries.value === 0) {
      return false;
    }
    if (typeof totalEntries.value !== 'undefined') {
      return false;
    }

    // total can exceed totalMax if creator updates supply
    return totalEntries.value! >= maxEntries.value!;
  });

  /**
   * Whether logged in user is in the allowlist if there is one
   */
  const isEligible = computed(() => {
    if (allowlistEnabled.value && allowlistRequirement.value?.merkleTreeId) {
      return !!signup.value?.requirements?.allowlist?.[0]?.complete;
    }
    return true;
  });

  /**
   * All inputs are disabled if:
   * user is not logged in
   * user is not eligible (if it's an allowlist giveaway)
   * the claim is not active
   * the claim is sold out
   */
  const disabled = computed(() => {
    return (
      (allowlistRequirement.value && allowlistEnabled.value && !isEligible.value) ||
      !isLoggedIn.value ||
      status.value !== 'active' ||
      isSoldOut.value
    );
  });

  /**
   * Combine instance requirements and signup data to get a flat list of requirements
   */
  const flatRequirements = computed<RequirementWithCollectorData[]>(() => {
    if (instanceRequirements.value && Object.entries(instanceRequirements.value).length) {
      return [
        ...Object.entries(instanceRequirements.value)
          .filter(([key, _requirements]) => {
            return key !== 'email';
          })
          .map(([key, typedInstanceRequirements]) => {
            return Object.entries(typedInstanceRequirements)
              .filter(([i, _req]) => {
                // Filter out null / inactive requirements
                return !!typedInstanceRequirements[parseInt(i)];
              })
              .map(([i, _req]) => {
                const signupRequirements =
                  signup.value?.requirements?.[key as GiveawayRequirementType];
                const signupRequirement = signupRequirements?.[parseInt(i)];
                return {
                  type: key as GiveawayRequirementType,
                  link: typedInstanceRequirements[parseInt(i)]!.link,
                  index: i,
                  userData: signupRequirement?.userData,
                  complete: signupRequirement?.complete,
                  error: signupRequirement?.error,
                };
              });
          })
          .flat(),
        /**
         *   Due to restrictions in current studio-apps schema, we put allowlistRequirement in publicData as a separate field rather than within the requirements object
         *   The allowlist requirement, however, is included in the signup response from redeem server as part of the requirements object
         */
        ...(allowlistEnabled.value && allowlistRequirement.value
          ? [
              {
                type: 'allowlist',
                index: 0,
                userData: signup.value?.requirements?.allowlist?.[0]?.userData, // Hardcoded to 0 because there should only be 1 allowlist requirement
                complete: signup.value?.requirements?.allowlist?.[0]?.complete,
                error: signup.value?.requirements?.allowlist?.[0]?.error,
              },
            ]
          : []),
      ].filter((r) => !!r) as RequirementWithCollectorData[];
    }

    return [];
  });

  const emailRequirements = computed(() => {
    if (!instanceRequirements.value || !instanceRequirements.value.email) {
      return [];
    }
    const emailRequirements = instanceRequirements.value.email;
    return emailRequirements
      .filter((_, i) => {
        // Filter out null / inactive requirements
        return !!emailRequirements[i];
      })
      .map((_, i) => {
        const signupRequirements = signup.value?.requirements?.email;
        const signupRequirement = signupRequirements?.[i];
        return {
          type: 'email',
          link: emailRequirements[i]!.link,
          index: i,
          userData: signupRequirement?.userData,
          complete: signupRequirement?.complete,
          error: signupRequirement?.error,
        };
      });
  });

  const twitterRequirements = computed(() => {
    return flatRequirements.value.filter((requirement) =>
      ['twitterFollow', 'twitterRetweet', 'twitterLike'].includes(requirement.type)
    );
  });
  const hasTwitterRequirements = computed(() => {
    return twitterRequirements.value.length > 0;
  });
  const twitterUsername = computed<string | undefined>(() => {
    const requirement = twitterRequirements.value.find((requirement) => {
      return !!requirement.userData?.username;
    });
    const requirementUsername = requirement?.userData?.username;
    return requirementUsername;
  });

  watch(isLoggedIn, async () => {
    refreshSignup();
  });

  watch([isLoggedIn, signup], async () => {
    refreshCurrentSignups();
  });

  return {
    initialize,
    refreshSignup,
    creatorEnsOrFormattedWalletAddress,
    ensOrFormattedWalletAddress,
    allowlistRequirement,
    initializedInstanceData,
    initialized,
    id,
    appId,
    title,
    image,
    description,
    slug,
    creator,
    location,
    startDate,
    endDate,
    status,
    isSoldOut,
    isLoggedIn,
    networkId,
    signup,
    totalEntries,
    maxEntries,
    instanceRequirements,
    allowlistDescription,
    allowlistLink,
    flatRequirements,
    emailRequirements,
    twitterRequirements,
    hasTwitterRequirements,
    twitterUsername,
    subscribed,
    isEligible,
    disabled,
  };
});

export type State = ReturnType<typeof useClaimStore>;
